import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { ReportActionSource } from '@amzn/handshake-painter-typescript-client/v1';
import styled from '@emotion/styled';
import { rio } from '@amzn/rio-design-tokens';
import { generatePath, useNavigate, useSearchParams } from 'react-router';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import Theme from '@amzn/meridian/theme';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Toaster from '@amzn/meridian/toaster';
import Alert from '@amzn/meridian/alert';
import { InverseButton } from 'src/common/buttons';
import { ControlledExpander } from 'src/common/expander';
import { Paragraph, SectionDivider } from 'src/common/styled';
import { getSma, Sma, smaDisplayName } from 'src/utils/user';
import { useAppInit, usePageContext } from 'src/hooks';
import { sendAuthMessage, sendCloseMessage } from 'src/utils/messages';
import { useAnalyticsContext } from 'src/common/metrics';
import { AuthStatus } from 'src/utils/auth';
import { getLinkedStatusAtSessionStart } from 'src/utils/getLinkedStatus';
import { routes } from 'src/utils/routes';
const Container = styled(Column) `
  background: linear-gradient(180deg, #ffedcc 0%, #f6c475 100%);
`;
const StyledBox = styled(Box) `
  border: none;
  border-radius: 8px;
`;
const StyledToast = styled.div `
  width: 100%;

  & > div {
    background: ${rio.semantic.color.background.primary};
    border: none;
    border-radius: 8px;

    & > [role='img'] {
      display: none;
    }
  }
`;
// Keep in light mode only
const StyledPagination = styled.div `
  position: relative;
  text-align: center;

  --swiper-pagination-color: ${rio.semantic.color.imagePagination.selected};
  --swiper-pagination-bullet-inactive-color: ${rio.semantic.color.imagePagination.unselected};
  --swiper-pagination-bullet-inactive-opacity: 1;
`;
/**
 * Order of operations:
 *  1. AppInit is called
 *     - On success, it sets the `linked` query param to equal appData.linked
 *  2. If appData.linked is true, show the "account linked" view
 *     - If the `linked` query param is false, that means the account is newly linked
 *     - If the `linked` query param is true, that means the account is already linked
 *  3. If appData.linked is false, show the campaign carousel
 */
export const LinkCampaignPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const rumClient = useAnalyticsContext();
    const { bundle } = usePageContext();
    const { data: appData } = useAppInit();
    // If peakRedirect is present, then we want to hide the "back to feed" button in favor of a
    // toast notification that will automatically redirect them to the peak deals storefront
    const isPeakRedirect = !!(appData === null || appData === void 0 ? void 0 : appData.linked) && searchParams.has('peakRedirect');
    const [toasts, setToasts] = useState(isPeakRedirect ? [{ id: 'redirectToPeakStorefront', timeout: 2000 }] : []);
    const isLinkedAtSessionStart = getLinkedStatusAtSessionStart(searchParams, appData === null || appData === void 0 ? void 0 : appData.linked);
    const client = getSma();
    const onGetStartedClick = () => {
        const redirectUrl = `${window.location.pathname}?${searchParams.toString()}`;
        rumClient.recordEvent('LINK_CAMPAIGN_LINK_BUTTON_CLICK', {
            url: redirectUrl,
            queryParams: searchParams.toString(),
        });
        sendAuthMessage(rumClient, AuthStatus.AUTHENTICATE, redirectUrl, ReportActionSource.LinkCampaignPage);
    };
    const onCloseClick = () => {
        rumClient.recordEvent('LINK_CAMPAIGN_CLOSE_BUTTON_CLICK', { queryParams: searchParams.toString() });
        sendCloseMessage(rumClient);
    };
    const onCloseToast = () => {
        setToasts([]);
        onCloseClick();
        // Redirect to peak deals storefront
        const path = generatePath(routes.storefrontPage, { id: 'peakdeals' });
        navigate(`${path}?${searchParams.toString()}`);
    };
    useEffect(() => {
        if (client === Sma.UNKNOWN) {
            rumClient.recordError('LINK_CAMPAIGN_UNKNOWN_SMA');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Run only once on mount
    }, []);
    // Linked use case
    if (appData === null || appData === void 0 ? void 0 : appData.linked) {
        return (_jsxs(Theme, { mode: "light", children: [_jsx(Toaster, { alignmentVertical: "bottom", toasts: toasts, onCloseToast: onCloseToast, children: (toast) => (_jsx(StyledToast, { "data-testid": "peak-redirect-toast", children: _jsx(Alert, { toast: true, onClose: toast.onClose, children: bundle.getMessage(toast.id) }) })) }), _jsxs(Container, { spacingInset: "350", spacing: "450", "data-testid": "link-campaign-page", children: [_jsxs(Column, { spacingInset: "none 450", children: [_jsx("img", { width: "100%", height: "auto", src: "https://m.media-amazon.com/images/G/01/ShopExternal/images/Retail_Lossing_4Star._CB569899047_.png" }), _jsx(Text, { type: "h200", alignment: "center", children: bundle.getMessage(!isLinkedAtSessionStart ? 'accountNowLinked' : 'accountAlreadyLinked') }), _jsx(Paragraph, { alignment: "center", children: bundle.getMessage('successfulLinkSecondaryText') })] }), !isPeakRedirect && (_jsx(InverseButton, { theme: { rio, mode: 'light' }, onClick: onCloseClick, "data-testid": "back-button", children: bundle.getMessage('backToFeed') }))] })] }));
    }
    // Unlinked use case
    return (_jsx(Theme, { mode: "light", children: _jsxs(Container, { spacingInset: "350", "data-testid": "link-campaign-page", children: [_jsxs(Swiper, { modules: [Pagination], pagination: {
                        enabled: true,
                        el: '.swiper-pagination',
                    }, loop: true, "data-testid": "link-campaign-swiper", children: [_jsx(SwiperSlide, { "data-testid": "campaign-slide", children: _jsxs(Column, { spacingInset: "none 450", children: [_jsx("img", { width: "100%", height: "auto", src: "https://m.media-amazon.com/images/G/01/ShopExternal/images/Retail_Lossing_Box_Delivery._CB569899047_.png" }), _jsx(Text, { type: "h200", alignment: "center", children: client === Sma.UNKNOWN
                                            ? bundle.getMessage('slide1Unknown')
                                            : bundle.formatMessage('slide1', { client: smaDisplayName[client] }) })] }) }), _jsx(SwiperSlide, { "data-testid": "campaign-slide", children: _jsxs(Column, { spacingInset: "none 450", children: [_jsx("img", { width: "100%", height: "auto", src: "https://m.media-amazon.com/images/G/01/ShopExternal/images/Prime_Bagnara_Delivery_VanOpen_wh._CB569899047_.png" }), _jsx(Text, { type: "h200", alignment: "center", children: bundle.getMessage('slide2') })] }) }), _jsx(SwiperSlide, { "data-testid": "campaign-slide", children: _jsxs(Column, { spacingInset: "none 450", children: [_jsx("img", { width: "100%", height: "auto", src: "https://m.media-amazon.com/images/G/01/ShopExternal/images/Retail_Dog_Box._CB569899047_.png" }), _jsx(Text, { type: "h200", alignment: "center", children: bundle.getMessage('slide3') })] }) })] }), _jsx(StyledPagination, { className: "swiper-pagination", "data-testid": "swiper-pagination" }), _jsx(InverseButton, { theme: { rio, mode: 'light' }, onClick: onGetStartedClick, "data-testid": "get-started-button", children: bundle.getMessage('getStarted') }), _jsxs(Column, { spacing: "300", spacingInset: "300 none", "data-testid": "faq-section", children: [_jsx(Text, { type: "h100", children: bundle.getMessage('faq') }), _jsxs(StyledBox, { type: "fill", backgroundColor: "white", children: [_jsx(ControlledExpander, { compact: true, title: bundle.getMessage('question1'), children: _jsx(Paragraph, { type: "b200", children: bundle.getMessage('answer1') }) }), _jsx(SectionDivider, {}), _jsx(ControlledExpander, { compact: true, title: bundle.getMessage('question2'), children: _jsx(Paragraph, { type: "b200", children: bundle.formatMessage('answer2', { client: smaDisplayName[client] }) }) })] })] })] }) }));
};
